import {
  ActionConfirmationModal,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Text,
  Switch,
} from "@/components/ui";
import type {
  PhaseActionDefinition,
  PhaseActionTaskTemplate,
  PhaseActionWidget,
  ReusableMatterPhase,
} from "@/lib/types";
import { type DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { Plus, Settings2, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PhaseActionWidgetEditor } from "./PhaseActionWidgetEditor";
import { PhaseContentPickerModal } from "./PhaseContentPickerModal";
import {
  ActionSettingsMenu,
  PhaseProjectActionCards,
} from "./PhaseProjectActionCards";
import { PhaseProjectPhaseNavigation } from "./PhaseProjectPhaseNavigation";
import { createPhaseInformationWidget } from "@/lib/data/static/phase-widget-catalog";
import { ClaimTypeScopeSelect } from "./ClaimTypeScopeSelect";
import { PhaseAssignmentSelect } from "@/components/features/matter/overview/matter-phases/PhaseAssignmentSelect";
import { getSamplePhaseStatus } from "./phase-project-sample-data";

export interface ProjectPhaseDraft {
  teamId?: string | null;
  teamLabel?: string | null;
  userId?: string | null;
  userLabel?: string | null;
  roleId?: string | null;
  roleLabel?: string | null;
  userAvailable?: boolean;
  localId: string;
  reusablePhaseId: string;
  claimTypeIds: string[];
  actionDefinitionIds: string[];
  actionClaimTypeIds: Record<string, string[]>;
  widgets: PhaseActionWidget[];
}

const claimTypesWithinPhase = (ids: string[], phaseIds: string[]) =>
  phaseIds.length === 0 ? ids : ids.filter((id) => phaseIds.includes(id));

export function PhaseProjectBuilder({
  phases,
  setPhases,
  reusablePhases,
  actionDefinitions,
  claimTypes,
  readOnly,
  canCreatePhase,
  onCreatePhase,
  canCreateAction,
  onCreateAction,
  actionTaskTemplates,
  onEditActionTasks,
  stacked,
  selectedPhaseId,
  onSelectedPhaseIdChange,
  preview = false,
  embedded = false,
  idPrefix = "",
  showSampleData,
  onShowSampleDataChange,
}: {
  actionTaskTemplates?: Record<string, PhaseActionTaskTemplate[]>;
  onEditActionTasks?: (actionId: string) => void;
  phases: ProjectPhaseDraft[];
  setPhases: (phases: ProjectPhaseDraft[]) => void;
  reusablePhases: ReusableMatterPhase[];
  actionDefinitions: PhaseActionDefinition[];
  claimTypes: { id: string; label: string }[];
  readOnly: boolean;
  canCreatePhase: boolean;
  onCreatePhase: () => void;
  canCreateAction: boolean;
  onCreateAction: (phaseId: string) => void;
  stacked: boolean;
  selectedPhaseId?: string;
  onSelectedPhaseIdChange: (phaseId?: string) => void;
  preview?: boolean;
  embedded?: boolean;
  idPrefix?: string;
  showSampleData: boolean;
  onShowSampleDataChange: (show: boolean) => void;
}) {
  const [contentPickerOpen, setContentPickerOpen] = useState(false);
  const [phaseToRemoveId, setPhaseToRemoveId] = useState<string>();
  const [editingWidgetId, setEditingWidgetId] = useState<string>();
  const [newActionGroupId, setNewActionGroupId] = useState<string>();
  const builderRef = useRef<HTMLDivElement>(null);
  const scrollToBuilderStart = useCallback(() => {
    const builder = builderRef.current;
    if (!builder) return;
    let parent = builder.parentElement;
    while (parent) {
      const overflow = window.getComputedStyle(parent).overflowY;
      if (
        (overflow === "auto" || overflow === "scroll") &&
        parent.scrollHeight > parent.clientHeight
      ) {
        parent.scrollTo({
          top:
            parent.scrollTop +
            builder.getBoundingClientRect().top -
            parent.getBoundingClientRect().top -
            16,
        });
        return;
      }
      parent = parent.parentElement;
    }
    window.scrollTo({
      top: window.scrollY + builder.getBoundingClientRect().top - 16,
    });
  }, []);
  const previousReusablePhaseIds = useRef(
    new Set(phases.map((phase) => phase.reusablePhaseId)),
  );
  useEffect(() => {
    const addedPhase = phases.find(
      (phase) => !previousReusablePhaseIds.current.has(phase.reusablePhaseId),
    );
    previousReusablePhaseIds.current = new Set(
      phases.map((phase) => phase.reusablePhaseId),
    );
    if (addedPhase) {
      onSelectedPhaseIdChange(addedPhase.localId);
      window.requestAnimationFrame(scrollToBuilderStart);
    }
  }, [phases, onSelectedPhaseIdChange, scrollToBuilderStart]);
  const activePhaseId = phases.some(
    (phase) => phase.localId === selectedPhaseId,
  )
    ? selectedPhaseId
    : phases[0]?.localId;
  const activeReusablePhases = useMemo(
    () => reusablePhases.filter((phase) => phase.lifecycle === "Active"),
    [reusablePhases],
  );
  const selectedPhaseIds = new Set(
    phases.map((phase) => phase.reusablePhaseId),
  );
  const availableReusablePhases = activeReusablePhases.filter(
    (phase) => !selectedPhaseIds.has(phase.id),
  );
  const selectedActionIds = new Set(
    phases.flatMap((phase) => phase.actionDefinitionIds),
  );
  const activePhase = phases.find((phase) => phase.localId === activePhaseId);
  const phaseToRemove = phases.find(
    (phase) => phase.localId === phaseToRemoveId,
  );
  const phaseToRemoveTitle = reusablePhases.find(
    (phase) => phase.id === phaseToRemove?.reusablePhaseId,
  )?.title;
  const initialWidgetTypes = useMemo(
    () =>
      activePhase?.widgets
        .filter((widget) => widget.type !== "ActionGroup")
        .map((widget) => widget.type) ?? [],
    [activePhase?.widgets],
  );
  const updatePhase = (
    localId: string,
    updater: (phase: ProjectPhaseDraft) => ProjectPhaseDraft,
  ) => {
    setPhases(
      phases.map((phase) =>
        phase.localId === localId ? updater(phase) : phase,
      ),
    );
  };
  const reorderActions = (phaseId: string, event: DragEndEvent) => {
    if (!event.over || event.active.id === event.over.id) return;
    updatePhase(phaseId, (phase) => {
      const groupedIds = new Set(
        phase.widgets.flatMap((widget) =>
          widget.type === "ActionGroup" ? widget.actionDefinitionIds : [],
        ),
      );
      const ungroupedIds = phase.actionDefinitionIds.filter(
        (id) => !groupedIds.has(id),
      );
      const from = ungroupedIds.indexOf(String(event.active.id));
      const to = ungroupedIds.indexOf(String(event.over?.id));
      if (from < 0 || to < 0) return phase;
      const movedIds = arrayMove(ungroupedIds, from, to);
      let nextIndex = 0;
      return {
        ...phase,
        actionDefinitionIds: phase.actionDefinitionIds.map((id) =>
          groupedIds.has(id) ? id : movedIds[nextIndex++],
        ),
      };
    });
  };
  const reorderPhases = (event: DragEndEvent) => {
    if (!event.over || event.active.id === event.over.id) return;
    const from = phases.findIndex((phase) => phase.localId === event.active.id);
    const to = phases.findIndex((phase) => phase.localId === event.over?.id);
    if (from >= 0 && to >= 0) setPhases(arrayMove(phases, from, to));
  };

  return (
    <div
      ref={builderRef}
      className={
        embedded
          ? "@container min-w-0"
          : "border-border/70 @container min-w-0 rounded-xl border"
      }
    >
      <div
        className={`border-border/70 flex flex-wrap items-center justify-between gap-3 border-b p-4 ${embedded ? "bg-card" : "bg-muted rounded-t-xl"}`}
      >
        <Text variant="body">
          {embedded ? "Matter phases" : "Project phases"}
        </Text>
        <div className="ml-auto flex items-center gap-2">
          <label className="text-muted-foreground flex items-center gap-2 text-sm">
            <Switch
              checked={showSampleData}
              onCheckedChange={onShowSampleDataChange}
              aria-label="Show sample data"
            />
            Sample data
          </label>
          {!readOnly ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="bg-background"
                  disabled={
                    !canCreatePhase && availableReusablePhases.length === 0
                  }
                >
                  <Plus className="size-4" aria-hidden="true" />
                  Add phase
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-48">
                {availableReusablePhases.map((phase) => (
                  <DropdownMenuItem
                    key={phase.id}
                    onSelect={() => {
                      const localId = crypto.randomUUID();
                      setPhases([
                        ...phases,
                        {
                          localId,
                          reusablePhaseId: phase.id,
                          claimTypeIds: [],
                          actionDefinitionIds: [],
                          actionClaimTypeIds: {},
                          widgets: [],
                        },
                      ]);
                      onSelectedPhaseIdChange(localId);
                    }}
                  >
                    {phase.title}
                  </DropdownMenuItem>
                ))}
                {canCreatePhase ? (
                  <>
                    {availableReusablePhases.length > 0 ? (
                      <DropdownMenuSeparator />
                    ) : null}
                    <DropdownMenuItem onSelect={onCreatePhase}>
                      <Plus className="size-4" aria-hidden="true" />
                      Create reusable phase
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      <div className="min-w-0 space-y-4 px-4 pt-4 pb-4">
        {phases.length > 0 ? (
          <PhaseProjectPhaseNavigation
            phases={phases}
            reusablePhases={reusablePhases}
            activePhaseId={activePhaseId}
            readOnly={readOnly}
            preview={preview}
            idPrefix={idPrefix}
            showSampleData={showSampleData}
            onSelect={(phaseId) => {
              onSelectedPhaseIdChange(phaseId);
            }}
            onReorder={reorderPhases}
          />
        ) : null}
        <div className="min-w-0 space-y-4">
          {phases.length === 0 ? (
            <div className="border-border rounded-lg border border-dashed py-8 text-center">
              <Text variant="subHeading">
                {preview
                  ? "This project has no phases yet."
                  : "No phases added"}
              </Text>
              {!readOnly ? (
                <Text variant="caption">
                  Add a reusable phase to begin building this project.
                </Text>
              ) : null}
            </div>
          ) : null}
          {phases.map((phase, phaseIndex) => {
            if (phase.localId !== activePhaseId) return null;
            const reusablePhase = reusablePhases.find(
              (item) => item.id === phase.reusablePhaseId,
            );
            const setPhaseClaimTypes = (claimTypeIds: string[]) => {
              const selectedIds =
                claimTypes.length > 0 &&
                claimTypes.every((claimType) =>
                  claimTypeIds.includes(claimType.id),
                )
                  ? []
                  : claimTypeIds;
              updatePhase(phase.localId, (current) => ({
                ...current,
                claimTypeIds: selectedIds,
                actionClaimTypeIds: Object.fromEntries(
                  Object.entries(current.actionClaimTypeIds).map(
                    ([actionId, ids]) => [
                      actionId,
                      claimTypesWithinPhase(ids, selectedIds),
                    ],
                  ),
                ),
                widgets: current.widgets.map((widget) => ({
                  ...widget,
                  claimTypeIds: claimTypesWithinPhase(
                    widget.claimTypeIds ?? [],
                    selectedIds,
                  ),
                })),
              }));
            };
            const groupedActionIds = new Set(
              phase.widgets.flatMap((widget) =>
                widget.type === "ActionGroup" ? widget.actionDefinitionIds : [],
              ),
            );
            const hasUngroupedActions = phase.actionDefinitionIds.some(
              (id) => !groupedActionIds.has(id),
            );
            const setActionClaimTypes = (
              actionId: string,
              claimTypeIds: string[],
            ) => {
              updatePhase(phase.localId, (current) => ({
                ...current,
                actionClaimTypeIds: {
                  ...current.actionClaimTypeIds,
                  [actionId]: claimTypeIds,
                },
              }));
            };
            const removeAction = (actionId: string) => {
              updatePhase(phase.localId, (current) => ({
                ...current,
                actionDefinitionIds: current.actionDefinitionIds.filter(
                  (id) => id !== actionId,
                ),
                actionClaimTypeIds: Object.fromEntries(
                  Object.entries(current.actionClaimTypeIds).filter(
                    ([id]) => id !== actionId,
                  ),
                ),
                widgets: current.widgets
                  .map((widget) => ({
                    ...widget,
                    actionDefinitionIds: widget.actionDefinitionIds.filter(
                      (id) => id !== actionId,
                    ),
                  }))
                  .filter(
                    (widget) =>
                      widget.type !== "ActionGroup" ||
                      widget.actionDefinitionIds.length > 0,
                  ),
              }));
            };
            return (
              <div
                key={phase.localId}
                id={`${idPrefix}phase-${phase.localId}`}
                role={preview ? "tabpanel" : undefined}
                className="border-border/70 bg-card overflow-hidden rounded-xl border shadow-xs"
              >
                <div className="bg-light-primary flex flex-wrap items-center gap-3 rounded-t-xl border-b p-4">
                  <Badge variant="outline" className="bg-background shrink-0">
                    {phaseIndex + 1}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Text variant="body">
                        {reusablePhase?.title ?? "Unavailable Phase"}
                      </Text>
                      {!readOnly ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="size-8 shrink-0"
                              aria-label={`Settings for ${reusablePhase?.title ?? "Phase"}`}
                            >
                              <Settings2
                                className="size-4"
                                aria-hidden="true"
                              />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onSelect={() => {
                                setPhaseToRemoveId(phase.localId);
                              }}
                            >
                              <Trash2 className="size-4" aria-hidden="true" />
                              Remove phase
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                      <div className="flex max-w-full min-w-0 items-center gap-2">
                        <div className={readOnly ? "min-w-0" : "w-56 shrink-0"}>
                          <PhaseAssignmentSelect
                            value={phase}
                            readOnly={readOnly}
                            onChange={(selection) => {
                              updatePhase(phase.localId, (current) => ({
                                ...current,
                                ...selection,
                              }));
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    {reusablePhase?.description ? (
                      <Text variant="caption" className="mt-0.5">
                        {reusablePhase.description}
                      </Text>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-muted-foreground text-sm">
                      Claim types
                    </span>
                    {readOnly ? (
                      <Badge
                        variant="outline"
                        className="bg-background max-w-60 truncate font-normal"
                      >
                        {phase.claimTypeIds.length === 0
                          ? "All claim types"
                          : claimTypes
                              .filter((item) =>
                                phase.claimTypeIds.includes(item.id),
                              )
                              .map((item) => item.label)
                              .join(", ")}
                      </Badge>
                    ) : (
                      <ClaimTypeScopeSelect
                        className="bg-background w-48"
                        claimTypes={claimTypes}
                        selectedIds={phase.claimTypeIds}
                        onChange={setPhaseClaimTypes}
                        label={`Claim types for ${reusablePhase?.title ?? "Phase"}`}
                      />
                    )}
                  </div>
                  {!readOnly ? (
                    <div className="ml-auto flex shrink-0 items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="bg-background"
                        onClick={() => {
                          setContentPickerOpen(true);
                        }}
                      >
                        <Plus className="size-4" aria-hidden="true" />
                        Add action or widget
                      </Button>
                    </div>
                  ) : null}
                </div>

                <div
                  id={`${idPrefix}phase-content-${phase.localId}`}
                  className="bg-muted/15 space-y-4 p-4"
                >
                  {phase.actionDefinitionIds.length === 0 &&
                  phase.widgets.length === 0 ? (
                    <div className="border-border rounded-lg border border-dashed py-8 text-center">
                      <Text variant="body">No actions or widgets added</Text>
                      <Text variant="caption">
                        {readOnly
                          ? "This phase has no content."
                          : "Use Add action or widget to build this phase."}
                      </Text>
                    </div>
                  ) : null}
                  {hasUngroupedActions ? (
                    <div className="space-y-3">
                      <Text variant="body" className="text-sm">
                        Phase actions
                      </Text>
                      <PhaseProjectActionCards
                        phase={phase}
                        actionDefinitions={actionDefinitions}
                        claimTypes={claimTypes}
                        readOnly={readOnly}
                        showSampleData={showSampleData}
                        samplePhaseStatus={getSamplePhaseStatus(
                          phaseIndex,
                          phases.length,
                        )}
                        actionTaskTemplates={actionTaskTemplates}
                        onEditActionTasks={onEditActionTasks}
                        onReorder={(event) => {
                          reorderActions(phase.localId, event);
                        }}
                        onClaimTypesChange={setActionClaimTypes}
                        onRemove={removeAction}
                      />
                    </div>
                  ) : phase.actionDefinitionIds.length === 0 &&
                    phase.widgets.length > 0 ? (
                    <div className="space-y-2">
                      <Text variant="body" className="text-sm">
                        Phase actions
                      </Text>
                      <Text variant="caption">No actions added.</Text>
                    </div>
                  ) : null}
                  {phase.widgets.length > 0 ? (
                    <PhaseActionWidgetEditor
                      widgets={phase.widgets}
                      showSeparator={hasUngroupedActions}
                      claimTypes={claimTypes.filter(
                        (claimType) =>
                          phase.claimTypeIds.length === 0 ||
                          phase.claimTypeIds.includes(claimType.id),
                      )}
                      onChange={(widgets) => {
                        updatePhase(phase.localId, (current) => ({
                          ...current,
                          widgets,
                        }));
                      }}
                      actions={phase.actionDefinitionIds.map((id) => ({
                        id,
                        title:
                          actionDefinitions.find((action) => action.id === id)
                            ?.title ?? "Unavailable Action",
                      }))}
                      readOnly={readOnly}
                      showSampleData={showSampleData}
                      newActionGroupId={newActionGroupId}
                      renderGroupActionControls={(actionId) =>
                        readOnly ? (
                          actionTaskTemplates?.[actionId]?.length ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="max-w-48 shrink-0 truncate"
                              title={actionTaskTemplates[actionId][0].title}
                              onClick={() => onEditActionTasks?.(actionId)}
                            >
                              Task: {actionTaskTemplates[actionId][0].title}
                            </Button>
                          ) : null
                        ) : (
                          <div className="flex shrink-0 items-center gap-2">
                            {onEditActionTasks ? (
                              actionTaskTemplates?.[actionId]?.length ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="max-w-48 truncate"
                                  title={actionTaskTemplates[actionId][0].title}
                                  onClick={() => {
                                    onEditActionTasks(actionId);
                                  }}
                                >
                                  Task: {actionTaskTemplates[actionId][0].title}
                                </Button>
                              ) : (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    onEditActionTasks(actionId);
                                  }}
                                >
                                  <Plus className="size-4" aria-hidden="true" />
                                  Add task
                                </Button>
                              )
                            ) : null}
                            <ActionSettingsMenu
                              actionId={actionId}
                              title={
                                actionDefinitions.find(
                                  (action) => action.id === actionId,
                                )?.title ?? "Unavailable Action"
                              }
                              phase={phase}
                              claimTypes={claimTypes.filter(
                                (claimType) =>
                                  phase.claimTypeIds.length === 0 ||
                                  phase.claimTypeIds.includes(claimType.id),
                              )}
                              actionTaskTemplates={actionTaskTemplates}
                              onEditActionTasks={onEditActionTasks}
                              onClaimTypesChange={setActionClaimTypes}
                              onRemove={removeAction}
                            />
                          </div>
                        )
                      }
                      stacked={stacked}
                      editingWidgetId={editingWidgetId}
                      onEditingWidgetIdChange={(widgetId, saved) => {
                        if (!widgetId && editingWidgetId === newActionGroupId) {
                          if (!saved) {
                            updatePhase(phase.localId, (current) => ({
                              ...current,
                              widgets: current.widgets.filter(
                                (widget) => widget.id !== newActionGroupId,
                              ),
                            }));
                          }
                          setNewActionGroupId(undefined);
                        }
                        setEditingWidgetId(widgetId);
                      }}
                    />
                  ) : phase.actionDefinitionIds.length > 0 ? (
                    <div className="border-border/70 space-y-2 border-t pt-4">
                      <Text variant="body" className="text-sm">
                        Phase widgets
                      </Text>
                      <Text variant="caption">No widgets added.</Text>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {activePhase && !readOnly ? (
        <PhaseContentPickerModal
          open={contentPickerOpen}
          onOpenChange={setContentPickerOpen}
          actions={actionDefinitions.filter(
            (action) =>
              !selectedActionIds.has(action.id) ||
              activePhase.actionDefinitionIds.includes(action.id),
          )}
          initialActionIds={activePhase.actionDefinitionIds}
          initialWidgetTypes={initialWidgetTypes}
          canCreateAction={canCreateAction}
          onCreateAction={() => {
            setContentPickerOpen(false);
            onCreateAction(activePhase.localId);
          }}
          onCreateActionGroup={() => {
            const id = crypto.randomUUID();
            setContentPickerOpen(false);
            updatePhase(activePhase.localId, (current) => ({
              ...current,
              widgets: [
                ...current.widgets,
                {
                  id,
                  type: "ActionGroup",
                  title: "",
                  actionDefinitionIds: [],
                },
              ],
            }));
            setNewActionGroupId(id);
            setEditingWidgetId(id);
          }}
          onApply={(actionIds, widgetTypes) => {
            updatePhase(activePhase.localId, (current) => ({
              ...current,
              actionDefinitionIds: [
                ...current.actionDefinitionIds.filter((id) =>
                  actionIds.includes(id),
                ),
                ...actionIds.filter(
                  (id) => !current.actionDefinitionIds.includes(id),
                ),
              ],
              actionClaimTypeIds: Object.fromEntries(
                actionIds.map((id) => [
                  id,
                  current.actionClaimTypeIds[id] ?? [],
                ]),
              ),
              widgets: [
                ...current.widgets
                  .map((widget) =>
                    widget.type === "ActionGroup"
                      ? {
                          ...widget,
                          actionDefinitionIds:
                            widget.actionDefinitionIds.filter((id) =>
                              actionIds.includes(id),
                            ),
                        }
                      : widget,
                  )
                  .filter(
                    (widget) =>
                      (widget.type === "ActionGroup" &&
                        widget.actionDefinitionIds.length > 0) ||
                      (widget.type !== "ActionGroup" &&
                        widgetTypes.includes(widget.type)),
                  ),
                ...widgetTypes
                  .filter(
                    (type) =>
                      !current.widgets.some((widget) => widget.type === type),
                  )
                  .map(createPhaseInformationWidget),
              ],
            }));
          }}
          stacked={stacked}
          showSampleData={showSampleData}
        />
      ) : null}
      <ActionConfirmationModal
        open={Boolean(phaseToRemoveId)}
        setOpen={(open) => {
          if (!open) setPhaseToRemoveId(undefined);
        }}
        title={`Remove ${phaseToRemoveTitle ?? "this phase"} from the project?`}
        description="This removes the phase, its actions, and its widgets from this project draft. The reusable phase remains available for other projects."
        buttonText="Remove phase"
        afterActionFn={() => {
          const index = phases.findIndex(
            (phase) => phase.localId === phaseToRemoveId,
          );
          if (index < 0) return;
          setPhases(
            phases.filter((phase) => phase.localId !== phaseToRemoveId),
          );
          onSelectedPhaseIdChange(
            phases[index + 1]?.localId ?? phases[index - 1]?.localId,
          );
          setPhaseToRemoveId(undefined);
        }}
      />
    </div>
  );
}
